import { computed, Injectable, Signal, signal } from '@angular/core';
import { FeatureFlagKey } from './feature-flag.model';

/**
 * Holds the set of feature flags resolved for the current tenant.
 * Flags are seeded by {@link TenantConfigService} from the tenant-config API
 * response, so no separate runtime fetch is required.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly _flags = signal<Record<string, boolean>>({});

  /** Readonly snapshot of all resolved flags for the current tenant. */
  readonly flags = this._flags.asReadonly();

  /**
   * Replaces the current flag map. Called by the tenant-config pipeline
   * whenever a fresh tenant configuration is loaded.
   *
   * @remarks
   * Accepts plain `string` keys because the server may include flags that
   * haven't yet shipped in the {@link FeatureFlagKey} union.
   */
  setFlags(flags: Record<string, boolean> | undefined | null): void {
    this._flags.set(flags ?? {});
  }

  /**
   * Returns a reactive signal that reports whether the given flag is enabled.
   * Use this in templates / computed pipelines.
   */
  hasFlag(key: FeatureFlagKey): Signal<boolean> {
    return computed(() => this._flags()[key] === true);
  }

  /**
   * Returns the current enabled state for the given flag.
   * Use this for imperative checks (e.g. inside guards).
   */
  isEnabled(key: FeatureFlagKey): boolean {
    return this._flags()[key] === true;
  }
}
